import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { setAmendmentTerms } from "services/contract-amendment.service";
import EmploymentTermsFields from "./EmploymentTermsFields";
import { describeAmendmentError, isTermsAlreadySet } from "./amendment-errors";
import { EMPTY_TERMS_DRAFT, hasAnyTerms, termsErrorKey, buildSetTermsPayload } from "./employment-terms";
import type { CurrentTerms } from "./employment-terms";

interface SetAmendmentTermsFormProps {
  companyId: string;
  amendmentId: string;
  employeeUserId?: string;
  currentTerms?: CurrentTerms;
  /** The terms are stored — or someone else stored them first; either way the panel reloads. */
  onFinished: () => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Adds the position / salary / hours terms to a signed amendment that was
 * written as free text only, so «Sutarties informacija» can follow it.
 */
export default function SetAmendmentTermsForm({
  companyId,
  amendmentId,
  employeeUserId,
  currentTerms,
  onFinished,
  onCancel,
}: SetAmendmentTermsFormProps) {
  const { t } = useTranslation();
  const [termsDraft, setTermsDraft] = useState(EMPTY_TERMS_DRAFT);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!hasAnyTerms(termsDraft)) {
      toast.error(t("amendment_terms_required"));
      return;
    }
    const termsError = termsErrorKey(termsDraft);
    if (termsError) {
      toast.error(t(termsError));
      return;
    }

    setSaving(true);
    try {
      await setAmendmentTerms(companyId, amendmentId, buildSetTermsPayload(termsDraft));
      toast.success(t("amendment_terms_saved"));
      await onFinished();
    } catch (err: any) {
      toast.error(describeAmendmentError(err, t));
      if (isTermsAlreadySet(err)) await onFinished();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3">
      <h3 className="text-sm font-semibold mb-2">{t("amendment_set_terms")}</h3>
      <EmploymentTermsFields
        companyId={companyId}
        employeeUserId={employeeUserId}
        currentTerms={currentTerms}
        draft={termsDraft}
        onChange={setTermsDraft}
        warnAboutOldPositions
      />
      <div className="flex gap-2">
        <button
          type="button"
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium disabled:opacity-50"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? "..." : t("save")}
        </button>
        <button
          type="button"
          className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium disabled:opacity-50"
          onClick={onCancel}
          disabled={saving}
        >
          {t("cancel")}
        </button>
      </div>
    </div>
  );
}
